// client/ui/UserProfile.js
import { ComponentBase } from "../core/ComponentBase.js";
import { avatarHelper } from "../helpers/avatarHelper.js"; // For the avatar
import { z } from "zod";
import UserProfileUpdateSchema from "../../schemas/collections/users.js"; // Import the base schema

export class UserProfile extends ComponentBase {
    static styles = `
        .user-profile-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 2rem 1rem;
            background-color: var(--bs-body-bg);
            border-radius: var(--bs-border-radius);
            max-width: 600px;
            margin: auto;
        }
        .user-profile-picture-container {
            position: relative;
            width: 120px;
            height: 120px;
            margin-bottom: 1rem;
            cursor: pointer;
        }
        .profile-picture-overlay {
            position: absolute;
            top: 0; left: 0; width: 100%; height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: opacity 0.3s ease;
            font-size: var(--ab-text-base);
            border-radius: 50%;
            text-align: center;
            padding: 0.5rem;
        }
        .user-profile-picture-container:hover .profile-picture-overlay {
            opacity: 1;
        }
        .user-info {
            text-align: center;
            width: 100%;
        }
        .user-info h2 {
            font-size: var(--ab-text-lg);
            font-weight: 500;
            margin-bottom: 0.25rem;
        }
        .user-info .text-muted {
            margin-bottom: 1rem;
        }
        .profile-btn-group {
            margin-top: 1.5rem;
            display: flex;
            gap: 0.75rem;
        }
        .verification-controls {
            width: 100%;
            max-width: 500px;
            margin-top: 1.5rem;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }
        .verification-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.5rem 0.75rem;
            border: 1px solid var(--bs-border-color-translucent);
            border-radius: var(--bs-border-radius);
            background-color: var(--bs-tertiary-bg);
        }
    `;

    constructor(config) {
        super(config);
        this.profileData = null;
        this.fileUpload = null;
        this.dataForm = null; // To hold the edit modal instance
        this.verificationState = { inProgress: false, channelType: null, identifier: null, otpRequestId: null };
        this.applyStyles();
    }

    static getDefaultConfig() {
        return {
            enableEdit: true,
            enablePasswordChange: true,
        };
    }

    async initialize() {
        this.fileUpload = await this.createManagedComponent('FileUpload', null, {
            getUploadKey: () => this.appClient.rpcClient.call('UserProfileRpc.getUploadKeyForProfilePicture'),
            instanceId: `profile-picture-upload-${this.config.instanceId}`,
            hidden: true,
        });
        this.fileUpload.on('uploadCompleted', this.handleAvatarUploaded.bind(this));
        await super.initialize();
    }

    async initAfterLogin() {
        await this.fetchUserProfile();
    }

    async fetchUserProfile() {
        try {
            this.profileData = await this.appClient.rpcClient.call('UserProfileRpc.getUserProfile');
            if (this.profileData) {
                this.render();
            }
        } catch (error) {
            this.log.error("Error fetching user profile:", error);
        }
    }

    async handleAvatarUploaded() {
        const uploadKey = this.fileUpload.getValue();
        if (uploadKey) {
            try {
                const updatedUser = await this.appClient.rpcClient.call('UserProfileRpc.updateProfilePicture', {
                    uploadKey: uploadKey
                });

                if (updatedUser) {
                    this.appClient.notifier.success("Profile picture updated.");
                    // Refresh the profile data to show the new picture
                    this.appClient.events.fireLocal('userProfilePictureUpdated', { avatarUrl: updatedUser.profilePicture });
                    await this.fetchUserProfile();
                } else {
                    throw new Error("Avatar update failed on server.");
                }
            } catch (error) {
                this.log.error("Error updating avatar:", error);
                this.appClient.notifier.error(`Failed to update avatar: ${error.message}`);
            } finally {
                // CRUCIAL: Clear the file uploader's state to allow for another upload
                this.fileUpload?.clear();
            }
        }
    }


    async render() {
        if (!this.container) {
            this.container = document.createElement("div");
        }
        this.container.innerHTML = '';
        this.container.className = "user-profile-container";

        if (!this.profileData) {
            this.container.innerHTML = `<div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div>`;
            return this.container;
        }

        // --- START OF FIX: Display Full Name ---
        const displayName = (this.profileData.firstName || this.profileData.lastName)
            ? `${this.profileData.firstName || ''} ${this.profileData.lastName || ''}`.trim()
            : this.profileData.username;
        // --- END OF FIX ---

        const avatarContainer = document.createElement('div');
        avatarContainer.className = 'user-profile-picture-container';
        avatarContainer.addEventListener('click', () => this.fileUpload.openFile());

        const avatar = avatarHelper.createAvatarElement({
            displayName: displayName,
            avatarUrl: this.profileData.profilePicture,
            sizeClass: 'large', // Use a large avatar
            size: 120
        });

        const overlay = document.createElement("div");
        overlay.className = "profile-picture-overlay";
        overlay.innerHTML = `<i class="bi bi-camera-fill me-1"></i> ${this.t('Change')}`;

        avatarContainer.appendChild(avatar);
        avatarContainer.appendChild(overlay);
        this.container.appendChild(avatarContainer);

        const userInfo = document.createElement("div");
        userInfo.className = "user-info";
        userInfo.innerHTML = `
            <h2>${displayName}</h2>
            <p class="text-muted">@${this.profileData.username}</p>
            <p><i class="bi bi-envelope-fill me-2"></i>${this.profileData.email || 'No email'}</p>
            <p><i class="bi bi-telephone-fill me-2"></i>${this.profileData.phoneNumber || 'No phone number'}</p>
        `;
        this.container.appendChild(userInfo);

        const verificationGroup = this.renderVerificationControls();
        this.container.appendChild(verificationGroup);

        const buttonGroup = document.createElement("div");
        buttonGroup.className = "profile-btn-group";
        if (this.config.enableEdit) {
            const editButton = document.createElement("button");
            editButton.textContent = this.t("Change Details");
            editButton.className = "btn btn-primary";
            editButton.addEventListener('click', () => this.showEditProfileModal());
            buttonGroup.appendChild(editButton);
        }
        if (this.config.enablePasswordChange) {
            const changePasswordButton = document.createElement("button");
            changePasswordButton.textContent = this.t("Change Password");
            changePasswordButton.className = "btn btn-secondary";
            changePasswordButton.addEventListener('click', () => this.showChangePasswordModal());
            buttonGroup.appendChild(changePasswordButton);
        }
        this.container.appendChild(buttonGroup);
        this.container.appendChild(await this.fileUpload.render());

        return this.container;
    }


    async showEditProfileModal() {
        if (this.dataForm) {
            this.dataForm.destroy();
            this.dataForm = null;
        }

        const formSchema = UserProfileUpdateSchema.pick({
            firstName: true,
            lastName: true,
        }).partial();

        this.dataForm = await this.createManagedComponent("DataForm", null, {
            schema: formSchema,
            mode: 'edit',
            layout: 'dialog',
            title: 'Edit Your Profile',
            instanceId: `user-profile-edit-form-${this.config.instanceId}`,
            fields: {
                firstName: { type: 'text', label: 'First Name' },
                lastName: { type: 'text', label: 'Last Name' },
            },
            actions: { save: true, cancel: true }
        });

        this.dataForm.setData(this.profileData);
        this.dataForm.show(); // This correctly shows the modal

        // --- FIX: Add robust event handlers that explicitly hide and destroy the form ---
        const handleDataUpdated = async (updatedData) => {
            try {
                await this.appClient.rpcClient.call('UserProfileRpc.updateUserProfile', updatedData);
                this.appClient.notifier.success("Profile updated successfully.");
                await this.fetchUserProfile();
            } catch (error) {
                this.appClient.notifier.error(`Update failed: ${error.message}`);
            } finally {
                // This block now runs regardless of success or failure
                if (this.dataForm) {
                    this.dataForm.hide(); // Gracefully hide the modal
                    this.dataForm.destroy();
                    this.dataForm = null;
                }
            }
        };

        const handleCancel = () => {
            if (this.dataForm) {
                this.dataForm.hide();
                this.dataForm.destroy();
                this.dataForm = null;
            }
        };

        this.dataForm.on('dataUpdated', handleDataUpdated);
        this.dataForm.on('cancel', handleCancel);
    }



    async showChangePasswordModal() {
        if (this.changePasswordForm) {
            this.changePasswordForm.destroy();
            this.changePasswordForm = null;
        }

        const changePasswordSchema = z.object({
            currentPassword: z.string().min(1, "Current password is required."),
            newPassword: z.string().min(8, "New password must be at least 8 characters."),
            confirmNewPassword: z.string().min(8, "Confirm password must be at least 8 characters.")
        }).refine((data) => data.newPassword === data.confirmNewPassword, {
            message: "New passwords do not match.",
            path: ["confirmNewPassword"]
        });

        this.changePasswordForm = await this.createManagedComponent("DataForm", null, {
            schema: changePasswordSchema,
            mode: 'create',
            layout: 'dialog',
            title: 'Change Password',
            instanceId: `user-profile-change-password-form-${this.config.instanceId}`,
            fields: {
                currentPassword: { type: 'password', label: 'Current Password' },
                newPassword: { type: 'password', label: 'New Password' },
                confirmNewPassword: { type: 'password', label: 'Confirm New Password' },
            },
            actions: { save: true, cancel: true }
        });

        this.changePasswordForm.show();

        const handleDataSaved = async (formData) => {
            try {
                const result = await this.appClient.rpcClient.call('UserProfileRpc.changePassword', {
                    currentPassword: formData.currentPassword,
                    newPassword: formData.newPassword
                });
                if (result.success) {
                    this.appClient.notifier.success("Password changed successfully!");
                    if (this.changePasswordForm) {
                        this.changePasswordForm.hide();
                        this.changePasswordForm.destroy();
                        this.changePasswordForm = null;
                    }
                } else {
                    this.appClient.notifier.error(result.error || "Failed to change password.");
                }
            } catch (error) {
                this.appClient.notifier.error(error.message);
            }
        };

        const handleCancel = () => {
            if (this.changePasswordForm) {
                this.changePasswordForm.hide();
                this.changePasswordForm.destroy();
                this.changePasswordForm = null;
            }
        };

        this.changePasswordForm.on('dataSaved', handleDataSaved);
        this.changePasswordForm.on('cancel', handleCancel);
    }

    renderVerificationControls() {
        const container = document.createElement('div');
        container.className = 'verification-controls';

        if (this.profileData.email && !this.profileData.isVerified) {
            const emailGroup = document.createElement('div');
            emailGroup.className = 'verification-item';
            emailGroup.innerHTML = `
                <div>
                    <i class="bi bi-envelope-exclamation-fill text-warning me-2"></i>
                    <span>${this.t('Email not verified')}</span>
                </div>
            `;
            const verifyEmailBtn = document.createElement('button');
            verifyEmailBtn.className = 'btn btn-warning btn-sm';
            verifyEmailBtn.textContent = this.t('Verify Now');
            verifyEmailBtn.onclick = () => this.startVerification('email', this.profileData.email);
            emailGroup.appendChild(verifyEmailBtn);
            container.appendChild(emailGroup);
        }

        if (this.profileData.phoneNumber && !this.profileData.isPhoneVerified) {
            const phoneGroup = document.createElement('div');
            phoneGroup.className = 'verification-item';
            phoneGroup.innerHTML = `
                <div>
                    <i class="bi bi-phone-fill text-warning me-2"></i>
                    <span>${this.t('Phone not verified')}</span>
                </div>
            `;
            const verifyPhoneBtn = document.createElement('button');
            verifyPhoneBtn.className = 'btn btn-warning btn-sm';
            verifyPhoneBtn.textContent = this.t('Verify Now');
            verifyPhoneBtn.onclick = () => this.startVerification('phone', this.profileData.phoneNumber);
            phoneGroup.appendChild(verifyPhoneBtn);
            container.appendChild(phoneGroup);
        }

        return container;
    }

    async startVerification(channelType, identifier) {
        this.verificationState.inProgress = true;
        this.verificationState.channelType = channelType;
        this.verificationState.identifier = identifier;

        try {
            const result = await this.appClient.rpcClient.call('AuthRpc.requestVerificationCode', {
                channelType: channelType === 'phone' ? 'sms' : 'email',
                identifier: identifier,
                contactInfo: {
                    firstName: this.profileData.firstName,
                    lastName: this.profileData.lastName
                }
            });

            if (result.success) {
                this.verificationState.otpRequestId = result.otpRequestId;
                await this.showOtpModal();
            } else {
                throw new Error("Failed to request OTP.");
            }
        } catch (error) {
            this.appClient.notifier.error(`Could not send verification code: ${error.message}`);
            this.resetVerificationState();
        }
    }

    async showOtpModal() {
        const code = await this.appClient.notifier.prompt(`A verification code has been sent to ${this.verificationState.identifier}. Please enter it below.`, {
            title: 'Enter Verification Code',
            inputType: 'text'
        });

        if (code) {
            await this.submitOtp(code);
        } else {
            this.resetVerificationState(); // User cancelled
        }
    }

    async submitOtp(code) {
        try {
            const result = await this.appClient.rpcClient.call('UserProfileRpc.verifyUserChannel', {
                otpRequestId: this.verificationState.otpRequestId,
                code: code,
                identifier: this.verificationState.identifier, // The email or phone number
                channelType: this.verificationState.channelType // 'email' or 'phone'
            });

            if (result.success) {
                this.appClient.notifier.success(result.message);
                await this.fetchUserProfile(); // Refresh profile to show verified status
            }
        } catch (error) {
            this.appClient.notifier.error(`Verification failed: ${error.message}`);
        } finally {
            this.resetVerificationState();
        }
    }


    resetVerificationState() {
        this.verificationState = {
            inProgress: false,
            channelType: null,
            identifier: null,
            otpRequestId: null
        };
    }



    destroy() {
        this.dataForm?.destroy();
        this.dataForm = null;
        this.changePasswordForm?.destroy();
        this.changePasswordForm = null;
        this.fileUpload?.destroy(); // Destroy the FileUpload instance if it exists
        this.fileUpload = null;
        super.destroy();
    }
}